import { Component, Input, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { SimpleChanges } from "@angular/core";
import { SharedService } from "src/app/services/shared.service";
import { OrderInterface } from "src/app/ts-interfaces/order-interface";
import { EXPERIENCE_TYPE, ALL_EXPERIENCE_TYPES } from "src/app/shared/constants/hosted-tracking-constants";
import { PositiveSystemStatus } from "src/app/shared/constants/system-status";

@Component({
  selector: "ds-order-system-status",
  templateUrl: "./order-system-status.component.html",
  styleUrls: ["./order-system-status.component.scss"],
})
export class OrderSystemStatus implements OnInit {
  constructor(private translate: TranslateService, private sharedService: SharedService) {}

  @Input() order!: OrderInterface;

  private labels = this.sharedService.getInitialLabels();

  public pickupHeading = this.labels.pickupDate;
  public pickupDate = "";
  public statusInfoHeader = "";
  public statusInfoText = "";
  isPositiveSystemStatus: boolean = false;

  ngOnInit(): void {
    this.positiveSystemStatusInfo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.order && changes.order.currentValue) {
      this.positiveSystemStatusInfo();
    }
  }

  positiveSystemStatusInfo() {
    this.isPositiveSystemStatus = (this.order && PositiveSystemStatus.includes(this.order.status)) || false;
    if (this.isPositiveSystemStatus) {
      this.statusInfoHeader = this.getTranslatedStatusInfo(this.order, "header");
      this.statusInfoText = this.getTranslatedStatusInfo(this.order);

      const formatDate = (pickup: any, type?: string): string => {
        if (!type) return `${pickup.weekday.substring(0, 3)}, ${pickup.month.substring(0, 3)} ${pickup.day}`;
        else if (type == "pickup")
          return `${pickup.weekday.substring(0, 3)}, ${pickup.month.substring(0, 3)} ${
            pickup.day
          }, ${this.labels.afterText.toLowerCase()} ${pickup.time}`;
        else
          return `${pickup.weekday.substring(0, 3)}, ${pickup.month.substring(0, 3)} ${
            pickup.day
          }, ${this.labels.arrivingByText.toLowerCase()} ${pickup.time}`;
      };

      const formatTimeRange = (start: any, end: any): string =>
        `${start.weekday.substring(0, 3)}, ${start.month.substring(0, 3)} ${start.day}, ${start.time} - ` +
        `${end.weekday.substring(0, 3)}, ${end.month.substring(0, 3)} ${end.day}, ${end.time}`;

      const formatSameDayRange = (start: any, end: any): string =>
        `${start.weekday.substring(0, 3)}, ${start.month.substring(0, 3)} ${start.day}, ${this.labels.betweenText} ` +
        `${start.time} - ${end.time}`;

      if (
        this.order?.orderAttributes?.experienceType == EXPERIENCE_TYPE["DELIVERY"] ||
        this.order?.orderAttributes?.experienceType == EXPERIENCE_TYPE["SHIPPING"]
      ) {
        const dropoffTime = this.order.dropoffTime?.endsAt;
        this.pickupHeading = this.labels.deliveryDateHeading;
        if (dropoffTime) {
          this.pickupDate = formatDate(this.sharedService.generateTimeToShow(dropoffTime, this.order), "delivery");
        } else {
          this.pickupDate = "Unavailable";
        }
      } else {
        this.pickupHeading = this.labels.pickupDate;
        const pickupTime = this.order?.pickupTime;

        if (pickupTime?.startsAt && pickupTime?.endsAt) {
          const start = this.sharedService.generateTimeToShow(pickupTime.startsAt, this.order);
          const end = this.sharedService.generateTimeToShow(pickupTime.endsAt, this.order);

          if (start && end) {
            const sameDay = start.day === end.day && start.month === end.month;
            this.pickupDate = sameDay ? formatSameDayRange(start, end) : formatTimeRange(start, end);
            this.pickupHeading = sameDay ? this.labels.pickupDate : this.labels.pickupBetween;
          }
          return;
        }

        if (pickupTime?.startsAt) {
          const start = this.sharedService.generateTimeToShow(pickupTime.startsAt, this.order);
          if (start) {
            this.pickupDate = formatDate(start, "pickup");
          }
          return;
        }
      }
    }
  }

  getTranslatedStatusInfo(order: any, infoType = ""): string {
    const experienceType = order?.orderAttributes?.experienceType || order?.type || "";
    const experienceTypeName = ALL_EXPERIENCE_TYPES[experienceType] || "";
    const defaultKey = "ORDER_NOT_YET_DISPATCHED";

    const keyPrefix = `HOSTED_TRACKING.STATUS_CARD.`;
    const suffix = infoType === "header" ? "_HEADER" : "_BODY";
    const statusKey = experienceType ? `${order.status}_${experienceTypeName}${suffix}` : `${defaultKey}${suffix}`;

    return this.translate.instant(`${keyPrefix}${statusKey}`) || "";
  }

  getOrderUnavailableHeader() {
    const experienceType = (this.order?.orderAttributes?.experienceType || this.order?.type || "") as string;
    const status = this.order?.status;
    return status ? this.sharedService.getTranslatedStatus(experienceType, status, "HEADER") : "";
  }

  getOrderUnavailableBody() {
    const experienceType = (this.order?.orderAttributes?.experienceType || this.order?.type || "") as string;
    const status = this.order?.status;
    return status ? this.sharedService.getTranslatedStatus(experienceType, status, "BODY") : "";
  }
}
